/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type PDPComparisonResponse,
  type PDPComparisonOptions,
  type PDPComparisonParams,
} from '../interface';

const URL = `${requestBaseURL}/ecomm/b2c/browse/v2/sku-comparisons`;

const getProductComparison = async (
  options: PDPComparisonParams,
  axiosInstance?: AxiosInstance
) => {
  const { parentBrandName, subBrandName, productId, skuId, storeNumber, vehicleId } = options;
  const response = await getAxios(axiosInstance).get<PDPComparisonResponse>(URL, {
    params: {
      parentBrandName,
      subBrandName,
      productId,
      skuId,
      storeNumber,
      vehicleId,
    },
  });
  return selector(response.data);
};

const selector = (data: PDPComparisonResponse): PDPComparisonResponse => data;

const { useData: useProductComparisonData, prefetch: prefetchProductComparisonData } = createQuery<
  PDPComparisonResponse,
  PDPComparisonParams
>('pdpComparison', async (options, axiosInstance) => getProductComparison(options, axiosInstance));

export const useProductComparison = (options: PDPComparisonOptions & { enabled?: boolean }) => {
  const { data: headerData } = useHeaderData();
  const storeNumber = headerData?.storeNumber;
  const vehicleId = getPreferredVehicle(headerData)?.catalogVehicleId ?? '0';

  return useProductComparisonData({
    ...options,
    storeNumber,
    vehicleId,
    enabled: (options.enabled ?? true) && !!headerData,
  });
};

export const prefetchProductComparison = (
  options: PDPComparisonParams,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetchProductComparisonData(queryClient, options, axiosInstance);
};
